
import { useState } from 'react'
import { Plus, Filter } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DietPlanTableV2 } from '@/components/diet-plans-v2/diet-plan-table-v2'
import { DietPlanBuilderV2 } from '@/components/diet-plans-v2/diet-plan-builder-v2'
import { AssignDietPlanDialog } from '@/components/diet-plans-v2/assign-diet-plan-dialog'
import { useCreateDietPlanV2 } from '@/api/diet-plans-v2'
import type { DietPlanV2 } from '@/types/diet-v2'
import { PageHeader } from '@/components/shared/page-header'

export default function DietPlansV2Page() {
  const [showBuilder, setShowBuilder] = useState(false)
  const [editingPlan, setEditingPlan] = useState<DietPlanV2 | null>(null)
  const [assigningPlan, setAssigningPlan] = useState<DietPlanV2 | null>(null)
  const [filters, setFilters] = useState<{ duration?: string; type?: string; isTemplate?: boolean }>({})

  const createMutation = useCreateDietPlanV2()

  const handleSavePlan = async (data: any) => {
    try {
      await createMutation.mutateAsync(data)
      setShowBuilder(false)
      setEditingPlan(null)
    } catch (error) {
      // Error handled by mutation
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Diet Plans"
        description="Manage multi-day diet plans with USDA food database"
      >
        <Button onClick={() => setShowBuilder(true)} className="w-full sm:w-auto">
          <Plus className="h-4 w-4 mr-2" />
          Create Diet Plan
        </Button>
      </PageHeader>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Duration</label>
              <Select value={filters.duration || 'all'} onValueChange={(v) => setFilters({ ...filters, duration: v === 'all' ? undefined : v })}>
                <SelectTrigger>
                  <SelectValue placeholder="All Durations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Durations</SelectItem>
                  <SelectItem value="day">Single Day</SelectItem>
                  <SelectItem value="week">Week (7 days)</SelectItem>
                  <SelectItem value="month">Month (30 days)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Plan Type</label>
              <Select value={filters.type || 'all'} onValueChange={(v) => setFilters({ ...filters, type: v === 'all' ? undefined : v })}>
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="weight-loss">Weight Loss</SelectItem>
                  <SelectItem value="muscle-gain">Muscle Gain</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Template</label>
              <Select
                value={filters.isTemplate === undefined ? 'all' : filters.isTemplate.toString()}
                onValueChange={(v) => setFilters({ ...filters, isTemplate: v === 'all' ? undefined : v === 'true' })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Plans" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Plans</SelectItem>
                  <SelectItem value="true">Templates Only</SelectItem>
                  <SelectItem value="false">User-Specific Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Diet Plans Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Diet Plans</CardTitle>
        </CardHeader>
        <CardContent>
          <DietPlanTableV2
            filters={filters}
            onEdit={(plan) => {
              setEditingPlan(plan)
              setShowBuilder(true)
            }}
            onAssign={(plan) => setAssigningPlan(plan)}
          />
        </CardContent>
      </Card>

      {/* Diet Plan Builder Dialog */}
      <Dialog open={showBuilder} onOpenChange={setShowBuilder}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Edit Diet Plan' : 'Create New Diet Plan'}</DialogTitle>
          </DialogHeader>
          <DietPlanBuilderV2
            onSave={handleSavePlan}
            initialData={
              editingPlan
                ? {
                    name: editingPlan.name,
                    type: editingPlan.type as 'weight-loss' | 'muscle-gain' | 'maintenance',
                    duration: editingPlan.duration,
                    description: editingPlan.description,
                    days: editingPlan.days,
                    isTemplate: editingPlan.isTemplate,
                  }
                : undefined
            }
          />
        </DialogContent>
      </Dialog>

      {/* Assign Diet Plan Dialog */}
      <AssignDietPlanDialog
        open={!!assigningPlan}
        onOpenChange={(open) => !open && setAssigningPlan(null)}
        plan={assigningPlan}
      />
    </div>
  )
}
